import React, { useCallback, useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import { toast } from 'sonner';
import {
  Bold, Italic, Heading1, Heading2, List, ListOrdered, Quote, Link as LinkIcon,
  Image as ImageIcon, Undo, Redo, Code, MonitorPlay
} from 'lucide-react';
import api from '../api';
import { extractErrorMessage } from '../lib/errors';
import Iframe, { extractEmbedSrc, isAllowedEmbedUrl } from './tiptap/Iframe';

// Toolbar button — kebab-case data-testid maps to the format action
const TB = ({ active, onClick, disabled, children, testid, title }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    data-testid={`rte-${testid}`}
    className={`w-8 h-8 rounded flex items-center justify-center text-sm border transition-colors ${
      active
        ? 'bg-[#1a1815] text-[#f5d67a] border-[#3a2f18]'
        : 'bg-transparent text-zinc-400 border-transparent hover:border-zinc-800 hover:text-zinc-100'
    } disabled:opacity-40 disabled:cursor-not-allowed`}
  >
    {children}
  </button>
);

/**
 * TipTap rich-text editor for the help-article admin UI.
 *
 * Uploads images through /admin/help-images (Emergent Object Storage). The
 * returned URL is inserted as an <img> node so the rendered article on the
 * public page can serve it via /api/help/images/{path} without auth.
 */
export default function RichTextEditor({ value, onChange, placeholder = 'Write your article…' }) {
  const fileRef = useRef(null);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { class: 'text-[#f5d67a] underline' } }),
      Image.configure({ HTMLAttributes: { class: 'rounded-lg border border-zinc-800 my-4' } }),
      Iframe,
      Placeholder.configure({ placeholder }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => onChange && onChange(editor.getHTML()),
    editorProps: {
      attributes: {
        class:
          'prose prose-invert prose-zinc max-w-none min-h-[360px] focus:outline-none ' +
          'prose-headings:font-display prose-a:text-[#f5d67a] prose-code:text-[#f5d67a]',
        'data-testid': 'rte-content',
      },
    },
  });

  // Sync incoming value when it comes from a fresh backend fetch
  useEffect(() => {
    if (!editor) return;
    if ((value || '') !== editor.getHTML()) {
      editor.commands.setContent(value || '', false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  const promptLink = useCallback(() => {
    if (!editor) return;
    const prev = editor.getAttributes('link').href || '';
    const url = window.prompt('URL', prev);
    if (url === null) return;
    if (url === '') { editor.chain().focus().unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url, target: '_blank', rel: 'noopener noreferrer' }).run();
  }, [editor]);

  const promptEmbed = useCallback(() => {
    if (!editor) return;
    const raw = window.prompt(
      'Paste a Scribe embed code, or a URL from Scribe / YouTube / Loom / Vimeo:',
      ''
    );
    if (!raw) return;
    const src = extractEmbedSrc(raw);
    if (!src) return toast.error('Could not find a URL in that paste. Copy the whole <iframe> tag or the share URL.');
    if (!isAllowedEmbedUrl(src)) {
      return toast.error('Only Scribe, YouTube, Loom, and Vimeo embeds are allowed for now.');
    }
    editor.chain().focus().setIframe({ src, title: 'Embedded content' }).run();
  }, [editor]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (!editor) return;
    const fd = new FormData();
    fd.append('file', f);
    const t = toast.loading(`Uploading ${f.name}…`);
    try {
      const r = await api.post('/admin/help-images', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      editor.chain().focus().setImage({ src: r.data.url, alt: f.name }).run();
      toast.success(`Inserted ${f.name}`, { id: t });
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Upload failed'), { id: t });
    }
  };

  if (!editor) return <div className="h-[440px] rounded-lg border border-zinc-800 bg-[#0a0a0b]"/>;

  return (
    <div className="rounded-lg border border-zinc-800 bg-[#0a0a0b]" data-testid="rich-text-editor">
      <div className="flex items-center gap-0.5 border-b border-zinc-800 p-2 flex-wrap">
        <TB testid="h1" title="Heading 1" active={editor.isActive('heading', { level: 1 })} onClick={()=>editor.chain().focus().toggleHeading({ level: 1 }).run()}><Heading1 size={16}/></TB>
        <TB testid="h2" title="Heading 2" active={editor.isActive('heading', { level: 2 })} onClick={()=>editor.chain().focus().toggleHeading({ level: 2 }).run()}><Heading2 size={16}/></TB>
        <div className="mx-1 h-5 w-px bg-zinc-800"/>
        <TB testid="bold" title="Bold" active={editor.isActive('bold')} onClick={()=>editor.chain().focus().toggleBold().run()}><Bold size={15}/></TB>
        <TB testid="italic" title="Italic" active={editor.isActive('italic')} onClick={()=>editor.chain().focus().toggleItalic().run()}><Italic size={15}/></TB>
        <TB testid="code" title="Inline code" active={editor.isActive('code')} onClick={()=>editor.chain().focus().toggleCode().run()}><Code size={15}/></TB>
        <div className="mx-1 h-5 w-px bg-zinc-800"/>
        <TB testid="bullet" title="Bulleted list" active={editor.isActive('bulletList')} onClick={()=>editor.chain().focus().toggleBulletList().run()}><List size={15}/></TB>
        <TB testid="ordered" title="Numbered list" active={editor.isActive('orderedList')} onClick={()=>editor.chain().focus().toggleOrderedList().run()}><ListOrdered size={15}/></TB>
        <TB testid="quote" title="Blockquote" active={editor.isActive('blockquote')} onClick={()=>editor.chain().focus().toggleBlockquote().run()}><Quote size={15}/></TB>
        <div className="mx-1 h-5 w-px bg-zinc-800"/>
        <TB testid="link" title="Add link" active={editor.isActive('link')} onClick={promptLink}><LinkIcon size={15}/></TB>
        <TB testid="image" title="Upload image" onClick={()=>fileRef.current?.click()}><ImageIcon size={15}/></TB>
        <TB testid="embed" title="Embed (Scribe, YouTube, Loom, Vimeo)" onClick={promptEmbed}><MonitorPlay size={15}/></TB>
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={onFile} className="hidden"/>
        <div className="ml-auto flex items-center gap-0.5">
          <TB testid="undo" title="Undo" onClick={()=>editor.chain().focus().undo().run()} disabled={!editor.can().undo()}><Undo size={15}/></TB>
          <TB testid="redo" title="Redo" onClick={()=>editor.chain().focus().redo().run()} disabled={!editor.can().redo()}><Redo size={15}/></TB>
        </div>
      </div>
      <div className="px-5 py-4">
        <EditorContent editor={editor}/>
      </div>
    </div>
  );
}
